import {
  Badge,
  Box,
  Button,
  Divider,
  Grid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
  Image,
  Link,
} from "@chakra-ui/react";
import { KYDScope } from "@common/interfaces/ICognito";
import { KYDEngageAutomationDefinition } from "@common/interfaces/KYDEngageAutomation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import actions from "src/actions";
import StatCard from "../Components/StatCard";
import {
  SUGGESTED_AUTOMATIONS,
  SuggestedAutomationRecipe,
  canShowSuggestedAutomations,
} from "../utils/automationBuilder";

const numberFormatter = new Intl.NumberFormat();

const formatNumber = (value?: number) => numberFormatter.format(value ?? 0);
const formatRevenue = (value?: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format((value ?? 0) / 100);
const formatRate = (value?: number) => `${((value ?? 0) * 100).toFixed(1)}%`;
const formatMultiplier = (value?: number) =>
  `${new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 1,
  }).format(value ?? 0)}x`;

const statusBadgeStyles: Record<
  KYDEngageAutomationDefinition["status"],
  { bg: string; color: string }
> = {
  ACTIVE: { bg: "#B8E8C6", color: "#1B5E38" },
  PAUSED: { bg: "#E2E8F0", color: "#334E68" },
  DRAFT: { bg: "#E9D8FD", color: "#553C9A" },
  ARCHIVED: { bg: "#EDF2F7", color: "#4A5568" },
};

const sumBy = (
  automations: KYDEngageAutomationDefinition[],
  selector: (automation: KYDEngageAutomationDefinition) => number | undefined
) =>
  automations.reduce((acc, automation) => acc + (selector(automation) || 0), 0);

const isSuggestionAlreadyCreated = (
  suggestion: SuggestedAutomationRecipe,
  existingAutomationNames: Set<string>
) => existingAutomationNames.has(suggestion.buildPayload().name.toLowerCase());

type SuggestedAutomationSetupCardProps = {
  suggestion: SuggestedAutomationRecipe;
  isSelected: boolean;
  isDisabled: boolean;
  onToggle: () => void;
};

const SuggestedAutomationSetupCard = ({
  suggestion,
  isSelected,
  isDisabled,
  onToggle,
}: SuggestedAutomationSetupCardProps) => (
  <Box
    as="button"
    type="button"
    textAlign="left"
    w="100%"
    p={6}
    borderWidth="1px"
    borderColor={isSelected ? "#141C2F" : "#DDDDDD"}
    borderRadius="16px"
    bg={isSelected ? "#F7FAFC" : "white"}
    boxShadow={isSelected ? "0 0 0 1px #141C2F" : "none"}
    cursor={isDisabled ? "not-allowed" : "pointer"}
    opacity={isDisabled ? 0.44 : 1}
    transition="border-color 0.15s ease, box-shadow 0.15s ease"
    onClick={() => {
      if (!isDisabled) {
        onToggle();
      }
    }}
    _hover={isDisabled ? undefined : { borderColor: "#141C2F" }}
  >
    <VStack alignItems="flex-start" spacing={3}>
      <VStack alignItems="flex-start" spacing={0}>
        <Text fontSize="sm" color="gray.600" fontWeight="500" lineHeight="110%">
          {suggestion.subtitle}
        </Text>
        <Text fontSize="md" fontWeight="600" color="gray.900" lineHeight="110%">
          {suggestion.title}
        </Text>
      </VStack>
      <Text fontSize="sm" color="gray.600" lineHeight="135%">
        {suggestion.description}
      </Text>
      <Text fontSize="sm" color="gray.800" fontWeight="500" lineHeight="110%">
        {suggestion.available ? "" : "Coming Soon"}
      </Text>
    </VStack>
  </Box>
);

const Automations = ({ permissions = [] }: { permissions?: KYDScope[] }) => {
  const [automations, setAutomations] = useState<
    KYDEngageAutomationDefinition[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [creatingSuggestionId, setCreatingSuggestionId] = useState<
    string | null
  >(null);
  const [selectedSuggestedIds, setSelectedSuggestedIds] = useState<Set<string>>(
    () => new Set()
  );
  const [isBulkCreating, setIsBulkCreating] = useState(false);
  const canShowSuggested = canShowSuggestedAutomations(permissions);
  const toast = useToast();
  const navigate = useNavigate();
  const fetchAutomations = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await actions.ADMIN.fetchEngageAutomations();
      setAutomations(response?.automations || []);
    } catch (error: any) {
      console.error("Failed to fetch engage automations", error);
      toast({
        title: "Unable to load automations",
        description: error?.message || "Please try again.",
        status: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchAutomations();
  }, [fetchAutomations]);

  const sortedAutomations = useMemo(
    () =>
      [...automations].sort((a, b) => b.updated_at.localeCompare(a.updated_at)),
    [automations]
  );

  const stats = useMemo(() => {
    const delivered = sumBy(
      sortedAutomations,
      (automation) => automation.metrics?.last_30_days?.deliveries
    );
    const opens = sumBy(
      sortedAutomations,
      (automation) => automation.metrics?.last_30_days?.unique_opens
    );
    const clicks = sumBy(
      sortedAutomations,
      (automation) => automation.metrics?.last_30_days?.unique_clicks
    );
    const revenue = sumBy(
      sortedAutomations,
      (automation) => automation.metrics?.last_30_days?.revenue
    );
    const tickets = sumBy(
      sortedAutomations,
      (automation) => automation.metrics?.last_30_days?.tickets
    );
    const unsubscribes = sumBy(
      sortedAutomations,
      (automation) => automation.metrics?.last_30_days?.unsubscribes
    );
    const sends = sumBy(
      sortedAutomations,
      (automation) => automation.metrics?.last_30_days?.sends
    );
    const spend = sumBy(
      sortedAutomations,
      (automation) => automation.metrics?.last_30_days?.spend
    );
    const roas = spend > 0 ? (revenue ?? 0) / spend : 0;

    return {
      delivered,
      revenue,
      spend,
      roas,
      tickets,
      openRate: delivered > 0 ? opens / delivered : 0,
      clicks,
      clickRate: delivered > 0 ? clicks / delivered : 0,
      unsubscribes,
      sends,
    };
  }, [sortedAutomations]);

  const lastUpdatedAt = useMemo(() => {
    if (sortedAutomations.length === 0) {
      return undefined;
    }
    return sortedAutomations[0].updated_at;
  }, [sortedAutomations]);

  const existingAutomationNames = useMemo(
    () =>
      new Set(automations.map((automation) => automation.name.toLowerCase())),
    [automations]
  );

  const isSetupMode = sortedAutomations.length === 0 && canShowSuggested;

  const selectableSuggestions = useMemo(
    () =>
      (canShowSuggested ? SUGGESTED_AUTOMATIONS : []).filter(
        (suggestion) =>
          suggestion.available &&
          !isSuggestionAlreadyCreated(suggestion, existingAutomationNames)
      ),
    [canShowSuggested, existingAutomationNames]
  );

  const selectedSuggestions = useMemo(
    () =>
      SUGGESTED_AUTOMATIONS.filter((suggestion) =>
        selectedSuggestedIds.has(suggestion.id)
      ),
    [selectedSuggestedIds]
  );

  const selectedCount = selectedSuggestions.length;

  const turnOnAutomationsLabel =
    selectedCount > 0
      ? `Turn On ${selectedCount} Automation${selectedCount === 1 ? "" : "s"}`
      : "Turn On Automations";

  const toggleSuggestedSelection = useCallback((suggestionId: string) => {
    setSelectedSuggestedIds((previous) => {
      const next = new Set(previous);
      if (next.has(suggestionId)) {
        next.delete(suggestionId);
      } else {
        next.add(suggestionId);
      }
      return next;
    });
  }, []);

  const createSelectedAutomations = useCallback(async () => {
    if (!canShowSuggested) {
      return;
    }

    const suggestionsToCreate = selectedSuggestions.filter(
      (suggestion) =>
        suggestion.available &&
        !isSuggestionAlreadyCreated(suggestion, existingAutomationNames)
    );

    if (suggestionsToCreate.length === 0) {
      return;
    }

    setIsBulkCreating(true);
    const createdNames: string[] = [];
    const failedNames: string[] = [];

    try {
      for (const suggestion of suggestionsToCreate) {
        try {
          await actions.ADMIN.createEngageAutomation(suggestion.buildPayload());
          createdNames.push(suggestion.subtitle);
        } catch (error: any) {
          console.error("Failed to create suggested automation", error);
          failedNames.push(suggestion.subtitle);
        }
      }

      if (createdNames.length > 0) {
        toast({
          title:
            createdNames.length === 1
              ? "Automation created"
              : `${createdNames.length} automations created`,
          description:
            createdNames.length === 1
              ? `${createdNames[0]} has been created.`
              : `${createdNames.join(", ")} have been created.`,
          status: "success",
        });
        setSelectedSuggestedIds(new Set());
        await fetchAutomations();
        navigate("/campaigns/automations");
      }

      if (failedNames.length > 0) {
        toast({
          title: "Unable to create some automations",
          description: `Failed for: ${failedNames.join(
            ", "
          )}. Please try again.`,
          status: "error",
        });
      }
    } finally {
      setIsBulkCreating(false);
    }
  }, [
    canShowSuggested,
    existingAutomationNames,
    fetchAutomations,
    navigate,
    selectedSuggestions,
    toast,
  ]);

  const createSuggestedAutomation = useCallback(
    async (suggestion: SuggestedAutomationRecipe) => {
      if (!canShowSuggested) {
        return;
      }

      setCreatingSuggestionId(suggestion.id);
      try {
        const response = await actions.ADMIN.createEngageAutomation(
          suggestion.buildPayload()
        );
        toast({
          title: "Automation created",
          description: `${suggestion.title} was created.`,
          status: "success",
        });
        await fetchAutomations();
        const automationId = response?.automation?.id;
        if (automationId) {
          navigate(`/campaigns/automations/${automationId}`);
        }
      } catch (error: any) {
        console.error("Failed to create suggested automation", error);
        toast({
          title: "Unable to create automation",
          description: error?.message || "Please try again.",
          status: "error",
        });
      } finally {
        setCreatingSuggestionId(null);
      }
    },
    [canShowSuggested, fetchAutomations, navigate, toast]
  );

  const isNoAccessState =
    !isLoading && automations.length === 0 && !canShowSuggested;

  if (isLoading) {
    return (
      <VStack py={8} w="100%">
        <Spinner />
      </VStack>
    );
  }

  return (
    <Box position="relative" w="100%" h="100%">
      {isNoAccessState && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={10}
        >
          <Box
            bg="white"
            borderRadius="16px"
            maxW="400px"
            w="full"
            mx={4}
            boxShadow="0px 20px 40px rgba(0, 0, 0, 0.15)"
            p={6}
          >
            <VStack spacing={3} textAlign="center">
              <Image src="/crown.png" alt="KYD" boxSize="28px" opacity={0.75} />
              <Text fontSize="xl" fontWeight="600" color="gray.800">
                Automations
              </Text>
              <Text fontWeight="600" color="gray.700">
                Not enabled for your organization.
              </Text>
              <Text color="gray.600" fontSize="sm" lineHeight="1.5">
                Automations let you trigger emails based on customer activity.
                Contact us to enable this for your organization.
              </Text>
              <Button
                as={Link}
                href="mailto:support@kydlabs.com"
                w="full"
                bg="#141C2F"
                color="white"
                borderRadius="14px"
                fontWeight="500"
                _hover={{ bg: "#000", textDecoration: "none" }}
              >
                Contact Us
              </Button>
            </VStack>
          </Box>
        </Box>
      )}
      <VStack
        alignItems="flex-start"
        py={4}
        spacing={5}
        w="100%"
        sx={
          isNoAccessState
            ? {
                filter: "blur(8px)",
                pointerEvents: "none",
                userSelect: "none",
              }
            : undefined
        }
      >
        <Text
          fontSize="32px"
          fontWeight="400"
          lineHeight="100%"
          letterSpacing="-0.02em"
        >
          KYD Automations
        </Text>

        <VStack mb={2} w="full" alignItems="flex-start" gap={0}>
          <Text
            fontSize="24px"
            fontWeight="600"
            lineHeight="100%"
            letterSpacing="-0.02em"
          >
            Last 30 Days
          </Text>
          {lastUpdatedAt && (
            <Text fontSize="xs" color="gray.500" fontWeight="400">
              Last updated on {dayjs(lastUpdatedAt).format("ddd, MMM Do")} at{" "}
              {dayjs(lastUpdatedAt).format("h:mmA")}
            </Text>
          )}
        </VStack>

        <Grid
          templateColumns={{
            base: "1fr",
            md: "repeat(2, 1fr)",
            lg: "repeat(4, 1fr)",
          }}
          gap={"24px"}
          alignItems="stretch"
          width="100%"
        >
          <StatCard title="Delivered" value={formatNumber(stats.delivered)} />
          <StatCard
            title="ROAS"
            value={formatMultiplier(stats.roas)}
            isKYD
            highlight
          />
          <StatCard
            title="Revenue"
            value={formatRevenue(stats.revenue)}
            isKYD
            highlight
          />
          <StatCard title="Tickets" value={formatNumber(stats.tickets)} />
          <StatCard title="Open Rate" value={formatRate(stats.openRate)} />
          <StatCard title="Clicks" value={formatNumber(stats.clicks)} />
          <StatCard
            title="Click Through Rate"
            value={formatRate(stats.clickRate)}
          />
          <StatCard
            title="Unsubscribes"
            value={formatNumber(stats.unsubscribes)}
          />
        </Grid>

        <Divider borderColor="#0000001A" mt="8px" mb="6px" />

        <Text
          fontSize="24px"
          fontWeight="600"
          lineHeight="100%"
          letterSpacing="-0.02em"
        >
          Automations
        </Text>

        <Box w="100%" overflowX="auto" maxWidth="100%">
          <Table
            variant="simple"
            size="lg"
            w="100%"
            minW="1080px"
            style={{ tableLayout: "auto" }}
          >
            <colgroup>
              <col />
              <col style={{ width: "140px" }} />
              <col style={{ width: "140px" }} />
              <col style={{ width: "140px" }} />
              <col style={{ width: "140px" }} />
              <col style={{ width: "140px" }} />
              <col style={{ width: "140px" }} />
              <col style={{ width: "140px" }} />
            </colgroup>
            <Thead>
              <Tr>
                <Th
                  px={0}
                  whiteSpace="nowrap"
                  color="#4A5568"
                  fontSize="12px"
                  fontWeight="700"
                  letterSpacing="0.06em"
                >
                  NAME
                </Th>
                <Th
                  whiteSpace="nowrap"
                  color="#4A5568"
                  fontSize="12px"
                  fontWeight="700"
                  letterSpacing="0.06em"
                  textAlign="center"
                >
                  STATUS
                </Th>
                <Th
                  isNumeric
                  whiteSpace="nowrap"
                  color="#4A5568"
                  fontSize="12px"
                  fontWeight="700"
                  letterSpacing="0.06em"
                >
                  # SENT
                </Th>
                <Th
                  isNumeric
                  whiteSpace="nowrap"
                  color="#4A5568"
                  fontSize="12px"
                  fontWeight="700"
                  letterSpacing="0.06em"
                >
                  % OPENED
                </Th>
                <Th
                  isNumeric
                  whiteSpace="nowrap"
                  color="#4A5568"
                  fontSize="12px"
                  fontWeight="700"
                  letterSpacing="0.06em"
                >
                  % CLICKED
                </Th>
                <Th
                  isNumeric
                  whiteSpace="nowrap"
                  color="#4A5568"
                  fontSize="12px"
                  fontWeight="700"
                  letterSpacing="0.06em"
                >
                  # UNSUBSCRIBES
                </Th>
                <Th
                  isNumeric
                  whiteSpace="nowrap"
                  color="#4A5568"
                  fontSize="12px"
                  fontWeight="700"
                  letterSpacing="0.06em"
                >
                  TICKETS
                </Th>
                <Th
                  isNumeric
                  whiteSpace="nowrap"
                  color="#4A5568"
                  fontSize="12px"
                  fontWeight="700"
                  letterSpacing="0.06em"
                >
                  REVENUE
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {sortedAutomations.map((automation) => {
                const metrics = automation.metrics?.last_30_days;
                const style = statusBadgeStyles[automation.status];
                return (
                  <Tr
                    key={automation.id}
                    onClick={() => {
                      navigate(`/campaigns/automations/${automation.id}`);
                    }}
                    _hover={{ bg: "#F7FAFC", cursor: "pointer" }}
                    style={{ textDecoration: "none" }}
                  >
                    <Td
                      px={0}
                      fontSize="15px"
                      fontWeight="600"
                      py={6}
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {automation.name}
                    </Td>
                    <Td whiteSpace="nowrap" textAlign="center">
                      <Badge
                        textTransform="uppercase"
                        px="12px"
                        py="5px"
                        borderRadius="10px"
                        bg={style.bg}
                        color={style.color}
                        fontSize="12px"
                        fontWeight="600"
                      >
                        {automation.status}
                      </Badge>
                    </Td>
                    <Td isNumeric fontSize="15px" whiteSpace="nowrap">
                      {formatNumber(metrics?.sends)}
                    </Td>
                    <Td isNumeric fontSize="15px" whiteSpace="nowrap">
                      {formatRate(metrics?.open_rate)}
                    </Td>
                    <Td isNumeric fontSize="15px" whiteSpace="nowrap">
                      {formatRate(metrics?.click_through_rate)}
                    </Td>
                    <Td isNumeric fontSize="15px" whiteSpace="nowrap">
                      {formatNumber(metrics?.unsubscribes)}
                    </Td>
                    <Td isNumeric fontSize="15px" whiteSpace="nowrap">
                      {formatNumber(metrics?.tickets)}
                    </Td>
                    <Td
                      isNumeric
                      fontSize="15px"
                      fontWeight="600"
                      whiteSpace="nowrap"
                    >
                      {formatRevenue(metrics?.revenue)}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>

        {canShowSuggested && (
          <Box pt={4} width="100%">
            <Text
              fontSize="24px"
              fontWeight="600"
              lineHeight="100%"
              letterSpacing="-0.02em"
              mb={4}
            >
              Suggested Automations
            </Text>
            <Grid
              templateColumns={{ base: "1fr", xl: "repeat(3, 1fr)" }}
              gap={6}
              width="100%"
            >
              {SUGGESTED_AUTOMATIONS.map((suggestion) => {
                const alreadyCreated = isSuggestionAlreadyCreated(
                  suggestion,
                  existingAutomationNames
                );
                return (
                  <VStack
                    key={suggestion.subtitle}
                    alignItems="stretch"
                    justifyContent="space-between"
                    spacing={5}
                    p={6}
                    border="1px solid #DDDDDD"
                    borderRadius="16px"
                    bg="white"
                  >
                    <VStack alignItems="flex-start" spacing={1}>
                      <Text fontSize="16px" color="#4A5568" fontWeight="600">
                        {suggestion.title}
                      </Text>
                      <Text
                        fontSize="16px"
                        fontWeight="600"
                        lineHeight="110%"
                        letterSpacing="-0.02em"
                      >
                        {suggestion.subtitle}
                      </Text>
                    </VStack>
                    <Text fontSize="14px" color="#4A5568" lineHeight="135%">
                      {suggestion.description}
                    </Text>
                    <Button
                      size="lg"
                      bg="#141C2F"
                      color="white"
                      borderRadius="14px"
                      fontWeight="500"
                      leftIcon={<Text fontSize="18px">▶</Text>}
                      isLoading={creatingSuggestionId === suggestion.id}
                      isDisabled={
                        !!creatingSuggestionId ||
                        alreadyCreated ||
                        !suggestion.available
                      }
                      onClick={() => createSuggestedAutomation(suggestion)}
                    >
                      {alreadyCreated
                        ? "Already Enabled"
                        : suggestion.available
                        ? "Turn On Automation"
                        : "Coming Soon"}
                    </Button>
                  </VStack>
                );
              })}
            </Grid>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default Automations;
